import { useLayoutEffect, useRef, useState } from 'react'


// Klick rein, Seitenzahl tippen, Enter (oder Klick weg) springt. Leere/ungueltige
// Eingabe = kein Sprung; zu grosse Zahl wird auf die letzte Seite geclampt
// (sofern bekannt). Breite waechst mit der Zahl der Stellen.
function PageJumper({ pageIndex, totalPages, setPage }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef(null)

  const commit = () => {
    setEditing(false)
    const num = parseInt(draft, 10)
    setDraft('')
    if (!Number.isFinite(num) || num < 1) return
    const clamped = totalPages > 0 ? Math.min(num, totalPages) : num
    if (clamped - 1 !== pageIndex) setPage(clamped - 1)
  }

  const digits = Math.max(2, String(totalPages || 999).length)

  return (
    <input
      ref={inputRef}
      type='text'
      inputMode='numeric'
      value={editing ? draft : String(pageIndex + 1)}
      onFocus={(e) => {
        setEditing(true)
        setDraft(String(pageIndex + 1))
        e.target.select()
      }}
      onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 6))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          setDraft(String(pageIndex + 1)) // Wert zuruecksetzen -> commit ist no-op
          e.currentTarget.blur()
        }
      }}
      title='Zu Seite springen'
      style={{ width: `${digits + 2.5}ch` }}
      className='ml-2 h-8 px-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-transparent text-textTitle font-semibold text-center outline-none cursor-text hover:border-primary focus:border-primary'
    />
  )
}


// Naechster horizontal scrollender Vorfahre. ALLE overflow-auto/scroll-Eltern
// zaehlen (auch ohne aktuelles Overflow): der ResizeObserver merkt spaeter,
// wenn die Tabelle nach einem Datenrefresh ueberhaupt erst breiter wird.
const findHScroller = (el) => {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    const { overflowX } = window.getComputedStyle(node)
    if (overflowX === 'auto' || overflowX === 'scroll') return node
  }
  return null
}


// Auto-Hide: die Bar geht auf, sobald die Maus in die unteren REVEAL_ZONE px des
// Scrollers kommt (etwas mehr als die Bar-Hoehe, damit sie aufklappt BEVOR die
// Maus sie erreicht). BOTTOM_EPS toleriert Sub-Pixel beim Erkennen des echten
// Seitenendes.
const REVEAL_ZONE = 64
const BOTTOM_EPS = 2
// Nachlaufzeit: nachdem die Maus die Zone verlaesst, bleibt die Bar noch so
// lange offen (gegen das nervige Sofort-Verschwinden). Kommt die Maus zurueck,
// wird das Ausblenden abgebrochen.
const HIDE_DELAY = 700


// Sticky am unteren Rand des Seiten-Scrollers: schwebt waehrend des Scrollens
// ueber dem Inhalt, dockt am echten Seitenende automatisch an (CSS sticky kann
// am Content-Ende nicht weiterrutschen, das Element sitzt dann an seiner
// natuerlichen Position). Layout: Prev/Next + aktuelle/gesamte Seite links,
// horizontale Proxy-Scrollbar in der Mitte (grosser Thumb in primary-gruen,
// dunkler Track), "Zeilen" + Pagesize-Chip rechts.
export default function PaginationBar({
  pageIndex,
  totalPages,
  setPage,
  pageSize,
  setPageSize,
  pageSizeOptions = [20, 50, 100],
  // Optional: explizite Steuerung des Next-Buttons unabhaengig vom totalPages-
  // Display. Im B2B-Modus (Server kennt nur has_next, keinen echten Total)
  // wird totalPages=null uebergeben (damit nichts neben dem Chip steht), aber
  // hasNext=true/false damit die Navigation trotzdem funktioniert.
  hasNext,
  className = '',
}) {
  const barRef = useRef(null)
  const scrollerRef = useRef(null)
  const trackRef = useRef(null)
  const [scroll, setScroll] = useState({ left: 0, width: 0, client: 0, top: 0, height: 0, clientH: 0 })
  // true, sobald die Maus in die untere Reveal-Zone des Scrollers faehrt
  const [hoverBottom, setHoverBottom] = useState(false)

  // useLayoutEffect: misst vor dem ersten Paint, sonst blitzt die Bar beim Laden
  // kurz sichtbar auf (Initial-State) und slidet dann weg.
  useLayoutEffect(() => {
    if (!barRef.current) return
    const scroller = findHScroller(barRef.current)
    scrollerRef.current = scroller
    if (!scroller) return
    const update = () => setScroll({
      left: scroller.scrollLeft,
      width: scroller.scrollWidth,
      client: scroller.clientWidth,
      top: scroller.scrollTop,
      height: scroller.scrollHeight,
      clientH: scroller.clientHeight,
    })
    update()
    scroller.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(scroller)
    if (scroller.firstElementChild) ro.observe(scroller.firstElementChild)

    // Maus-Naehe zur Unterkante (rAF-gedrosselt, setState bailt bei gleichem
    // Bool ohnehin aus). lastY wird stets aktualisiert, im Frame ausgewertet.
    let raf = 0
    let lastY = 0
    let hideTimer = 0
    const reveal = () => {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = 0 }
      setHoverBottom(true)
    }
    const scheduleHide = () => {
      if (hideTimer) return
      hideTimer = setTimeout(() => { hideTimer = 0; setHoverBottom(false) }, HIDE_DELAY)
    }
    const onMove = (e) => {
      lastY = e.clientY
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const rect = scroller.getBoundingClientRect()
        const dist = rect.bottom - lastY
        if (dist >= 0 && dist <= REVEAL_ZONE) reveal()
        else scheduleHide()
      })
    }
    const onLeave = () => scheduleHide()
    scroller.addEventListener('mousemove', onMove)
    scroller.addEventListener('mouseleave', onLeave)

    return () => {
      scroller.removeEventListener('scroll', update)
      scroller.removeEventListener('mousemove', onMove)
      scroller.removeEventListener('mouseleave', onLeave)
      if (raf) cancelAnimationFrame(raf)
      if (hideTimer) clearTimeout(hideTimer)
      ro.disconnect()
    }
  }, [])

  // Fortschritts-Fill statt wanderndem Thumb: ganz links ist der gruene Balken
  // KOMPLETT unsichtbar (das Dauer-Neon war unangenehm), beim Scrollen zieht er
  // sich proportional von links nach rechts auf. Ohne Overflow bleibt der Track
  // leer.
  const hasHScroll = scroll.width > scroll.client + 1
  const fillPct = hasHScroll
    ? (scroll.left / (scroll.width - scroll.client)) * 100
    : 0

  // "Ganz unten" = vertikaler Scroll am Ende -- oder es gibt gar keinen
  // vertikalen Scroll (kurze Seite), dann ist die Bar immer sichtbar. Sonst
  // bleibt sie versteckt, bis die Maus in die untere Zone faehrt.
  const atBottom = scroll.height - (scroll.top + scroll.clientH) <= BOTTOM_EPS
  const barVisible = atBottom || hoverBottom

  // Klick/Ziehen auf dem Track scrubbt direkt zur Position
  const scrubTo = (clientX) => {
    if (!scrollerRef.current || !trackRef.current) return
    const rect = trackRef.current.getBoundingClientRect()
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const max = scrollerRef.current.scrollWidth - scrollerRef.current.clientWidth
    scrollerRef.current.scrollLeft = frac * max
  }

  const onTrackPointerDown = (e) => {
    if (!hasHScroll) return
    e.preventDefault()
    scrubTo(e.clientX)
    const onMove = (ev) => scrubTo(ev.clientX)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const hasPrev = pageIndex > 0
  const realHasNext = hasNext != null
    ? hasNext
    : (totalPages > 0 && pageIndex < totalPages - 1)
  const navBtn = 'h-8 w-8 rounded-md border border-neutral-200 dark:border-neutral-800 flex items-center justify-center text-textTitle hover:border-primary disabled:opacity-30 disabled:hover:border-neutral-200 dark:disabled:hover:border-neutral-800'

  return (
    <footer
      ref={barRef}
      className={`sticky bottom-0 left-0 z-20 bg-background border-t border-gray-200 dark:border-gray-700 transition-all duration-200 ease-out ${barVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'} ${className}`}
    >
      <div className='flex items-center gap-2 px-3 py-2 text-sm'>
        <button
          type='button'
          onClick={() => hasPrev && setPage(pageIndex - 1)}
          disabled={!hasPrev}
          className={navBtn}
          aria-label='Vorige Seite'
        >
          <i className='bx bx-chevron-left text-lg'></i>
        </button>
        <button
          type='button'
          onClick={() => realHasNext && setPage(pageIndex + 1)}
          disabled={!realHasNext}
          className={navBtn}
          aria-label='Naechste Seite'
        >
          <i className='bx bx-chevron-right text-lg'></i>
        </button>

        <PageJumper pageIndex={pageIndex} totalPages={totalPages} setPage={setPage} />
        {/* Total nur anzeigen, wenn sie wirklich bekannt ist -- waehrend Loads
            oder Resets liefert displayTotalPages null, dann steht da nichts. */}
        {totalPages > 0 && (
          <span className='text-gray-500'>{totalPages}</span>
        )}

        {/* Proxy-Scrollbar als Fortschritts-Fill (links unsichtbar, zieht sich
            beim Scrollen auf) mit greifbarem Knopf am Fill-Ende; Klick/Ziehen
            auf Track oder Knopf scrubbt (Events bubbeln zum Track-Handler) */}
        <div
          ref={trackRef}
          onPointerDown={onTrackPointerDown}
          // ml-5: der Griff ragt bei Position 0 (-translate-x-1/2) ueber die
          // linke Track-Kante hinaus -- mehr Luft zur Gesamtseitenzahl
          className={`flex-1 ml-5 mr-2 relative h-2.5 bg-neutral-200 dark:bg-neutral-800 rounded-full ${hasHScroll ? 'cursor-pointer' : ''}`}
        >
          <div
            className='absolute top-0 bottom-0 left-0 bg-primary rounded-full pointer-events-none'
            style={{ width: `${fillPct}%` }}
          />
          {hasHScroll && (
            // Griff im Chip-Stil der Prev/Next-Buttons (gleiche Border/Hover)
            // mit dem Grip-Icon aus dem Column Chooser
            <div
              className='absolute top-1/2 h-5 w-7 -translate-y-1/2 -translate-x-1/2 rounded-md bg-background border border-neutral-200 dark:border-neutral-700 shadow-sm hover:border-primary cursor-grab active:cursor-grabbing flex items-center justify-center'
              style={{ left: `${fillPct}%` }}
            >
              <i className='bx bx-grid-vertical text-xs text-neutral-400 dark:text-neutral-500 leading-none'></i>
            </div>
          )}
        </div>

        <span className='text-gray-500'>Zeilen</span>
        <div className='relative'>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className='h-8 pl-3 pr-7 rounded-md border border-neutral-200 dark:border-neutral-800 bg-transparent text-textTitle font-semibold appearance-none cursor-pointer hover:border-primary focus:outline-none focus:border-primary'
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <i className='bx bx-chevron-down text-base text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none'></i>
        </div>
      </div>
    </footer>
  )
}
